import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateUser } from '@xhype/card-app-sdk';
import { useMagic, useSmartWallet } from '@package/smart-wallet/providers';
import { AxiosError } from 'axios';

import { useAuthenticate } from '../auth';
import { useReferralCode } from '../referral';

/* Types */
export type TRegisterProps = {
  isConnected: boolean;
  onSuccess: () => void;
};

/* Hook */
export const useRegister = ({ isConnected, onSuccess }: TRegisterProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [isUserCreated, setIsUserCreated] = useState(true);
  const { smartAccountAddress, logout } = useSmartWallet();
  const hasAuthenticatedRef = useRef(false);
  const isProcessingRef = useRef(false);
  const { magic } = useMagic();

  // Referral code hook
  const { assignReferralCode } = useReferralCode();

  /* Mutations */
  const createUser = useCreateUser({
    onError: async (error: any) => {
      const errorMsg =
        error?.response?.data?.msg?.toString() || 'Unknown error';
      console.log('Create user: Error', errorMsg);
      setError(errorMsg);

      // If email already exists in provider, show modal before logout
      if (errorMsg.includes('Email already exists in provider')) {
        console.warn('[useRegister] Email conflict detected, showing modal...');
        setShowEmailExistsModal(true);
      }
    },
    onSuccess: (data) => {
      setIsUserCreated(true);
      console.log('Create user: Successfully created', data);
    },
  });

  /* Callbacks */
  const createNewUser = useCallback(async () => {
    setIsLoading(true);
    console.log('[useRegister] Creating new user...');
    if (!magic) {
      console.error('[useRegister] Magic SDK not initialized');
      setIsLoading(false);
      setError('Magic SDK not initialized');
      return;
    }
    const magicData = await magic.user.getInfo();
    if (!smartAccountAddress || !magicData.email) {
      setIsLoading(false);
      setError('Missing smart account address or email');
      return;
    }

    try {
      await createUser.mutateAsync({
        email: magicData.email,
        address: smartAccountAddress,
        role: 'USER',
      });
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    } finally {
      setIsLoading(false);
    }
  }, [smartAccountAddress, magic, createUser]);

  const { authenticate, isAuthenticating } = useAuthenticate({
    onSuccess: () => {
      console.log('[useRegister] User authenticated successfully');
      onSuccess();
    },
    onError: async (error: any) => {
      console.error(
        `Get User: Axios error occurred - ${error?.response?.data?.msg.toString()}`,
      );
      if (error?.response?.data?.msg?.includes('User not found')) {
        setIsUserCreated(false);
        console.log(
          '[useRegister] User not found, proceeding to create user...',
        );
        await createNewUser();
      } else {
        console.log('[useRegister] Authentication error:', error);
        setError(error?.response?.data?.msg.toString());
      }
    },
  });

  /* Effects */
  useEffect(() => {
    const processAuthentication = async () => {
      // Guard clause: check if we should process
      if (
        !isUserCreated ||
        !isConnected ||
        hasAuthenticatedRef.current ||
        isProcessingRef.current
      ) {
        return;
      }

      // Mark as processing to prevent race conditions
      isProcessingRef.current = true;
      hasAuthenticatedRef.current = true;
      setIsLoading(true);

      try {
        // Step 1: Authenticate user (this updates AuthStore)
        console.log('[useRegister] Step 1/2: Authenticating user...');
        const userId = await authenticate();
        console.log('[useRegister] ✅ User authenticated successfully');

        // Step 2: Assign referral code (only after authentication is complete)
        console.log('[useRegister] Step 2/2: Assigning referral code...');
        await assignReferralCode(userId);
        console.log('[useRegister] ✅ Referral code assigned successfully');

        console.log(
          '[useRegister] 🎉 Authentication flow completed successfully',
        );
      } catch (error) {
        console.error('[useRegister] ❌ Error in authentication flow:', error);
        if (
          error instanceof AxiosError &&
          !error.response?.data?.msg?.includes('User not found')
        ) {
          setError(
            error instanceof Error ? error.message : 'Authentication failed',
          );
        }
        // Reset refs on error to allow retry
        hasAuthenticatedRef.current = false;
        isProcessingRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    processAuthentication();
  }, [isConnected, authenticate, assignReferralCode, isUserCreated]);

  /* Callbacks */
  const handleCloseEmailExistsModal = useCallback(async () => {
    setShowEmailExistsModal(false);
    console.log('[useRegister] User confirmed, logging out...');
    await logout();
  }, [logout]);

  /* Return */
  return {
    isLoading: isLoading || isAuthenticating,
    error,
    showEmailExistsModal,
    handleCloseEmailExistsModal,
  };
};
